// Single source of truth for an engine/session's DISPLAYED connection status, anchored to the
// `connectedAt` timestamp (stamped on a confirmed connection and refreshed by the pod heartbeat while
// connected) rather than jittery live MQTT presence or the genuinely-ambiguous lastSeen. Used by BOTH
// the Engines-Management list (selectDisplayEngines) and the top-bar connection indicator
// (selectIsCurrentUserSessionConnected) so the two never diverge. Within the grace window after a
// confirmed connection we MASK ALL transient churn — including setup/pairing/QR states a gateway upgrade
// or spot-node reclaim drives the pod back through — so the user sees a steady 'connected' instead of a
// flicker; sends retry and resume in the background. Only after the window elapses with no heartbeat
// refresh (pod genuinely gone) does the real status surface. Dependency-free so it can be imported
// anywhere without a selector cycle.

// Grace window: how long after the last confirmed-connected signal (connectedAt) we keep masking
// transient states as 'connected'. The pod refreshes connectedAt on its 5-min heartbeat while connected,
// so 10 min = two heartbeats of slack — a live pod never reads stale, and a single gateway-upgrade /
// spot-reclaim cycle (well under 10 min) is fully hidden. Scheduled sends have a ~30-min post-due grace,
// so masking a sub-window blip can't cause a missed message — showing 'connected' stays truthful.
export const CONNECTED_FRESHNESS_MS = 10 * 60 * 1000;

// Statuses that represent genuine setup / pairing / logged-out states. Outside the connectedAt grace
// window these are shown as-is — they reflect a real state the user must act on (scan QR, finish setup).
// Inside the window they're masked (see deriveDisplayStatus): a confirmed-connected session briefly
// cycling through them is churn, not a real call to action.
export const SETUP_OR_PAIRING_STATUSES = [
  'initializing', 'browser_started', 'navigating', 'waiting_for_whatsapp_page',
  'syncing', 'qrcode', 'PAIRING', 'unpaired', 'logout', 'error',
  // Pairing-code ("link with phone number") linking states (#302). Surface them as-is
  // outside the connected grace window so the row reflects the real pairing progress
  // instead of falling through to 'disconnected'.
  'awaiting_phone', 'pairing_code',
];

export interface DisplayStatusSession {
  status?: string | null;
  // Primary anchor: stamped server-side when the session transitions to 'connected' and refreshed by the
  // 5-min pod heartbeat while it stays connected. Only advances on a *confirmed* connection — unlike
  // lastSeen it never keeps a genuine QR-needed session looking alive.
  connectedAt?: string | number | Date | null;
  // Legacy fallback inputs, used only when connectedAt is absent (pod not yet rolled to the emitting image).
  wasConnected?: boolean;
  lastSeen?: string | number | Date | null;
}

/**
 * Displayed connection status for a session, anchored to the connectedAt grace window.
 * @param now epoch ms — pass a throttled tick so a stale engine flips to 'disconnected' over time.
 */
export function deriveDisplayStatus(session: DisplayStatusSession | null | undefined, now: number): string {
  if (!session) return 'disconnected';

  // Primary path: within the grace window after a confirmed connection → mask ALL transient churn
  // (setup/pairing/QR/blip) and report 'connected'. connectedAt only advances on a real connection and
  // is heartbeat-refreshed while connected, so this can't keep a genuinely-disconnected pod looking alive.
  const connectedAtMs = session.connectedAt ? new Date(session.connectedAt).getTime() : 0;
  if (connectedAtMs > 0 && now - connectedAtMs < CONNECTED_FRESHNESS_MS) {
    return 'connected';
  }

  // Outside any connected window: surface genuine setup/pairing/QR/logout as-is so the user can act.
  if (session.status && SETUP_OR_PAIRING_STATUSES.includes(session.status)) {
    return session.status;
  }

  // Fallback for pods not yet emitting connectedAt: the pre-existing lastSeen ride-through for brief
  // NON-setup blips (stream_disconnected/OPENING). Deliberately narrower than the connectedAt mask — it
  // runs only after the setup-state check above, so it never masks a genuine QR (lastSeen refreshes
  // during QR loops and would lie). Remove once the fleet fully emits connectedAt.
  if (connectedAtMs === 0 && session.wasConnected) {
    const lastSeenMs = session.lastSeen ? new Date(session.lastSeen).getTime() : 0;
    if (lastSeenMs > 0 && now - lastSeenMs < CONNECTED_FRESHNESS_MS) {
      return 'connected';
    }
  }

  return 'disconnected';
}

/** Convenience boolean for the binary connection indicator. */
export function isDisplayConnected(session: DisplayStatusSession | null | undefined, now: number): boolean {
  return deriveDisplayStatus(session, now) === 'connected';
}

// `now` selector input: passed in (defaults to Date.now()) so a component can supply a throttled tick.
// This lets a *stale* engine flip to 'disconnected' over time even with no new redux session update.
// Shared so both selectDisplayEngines and the top-bar indicator selector re-evaluate identically.
export const selectNow = (_state: unknown, now?: number): number => now ?? Date.now();
